// firestoreService.js
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    writeBatch,
    query,
    where,
    orderBy,
    onSnapshot,
    serverTimestamp
  } from "firebase/firestore";
  import { db } from "./firebaseConfig";
  
  /**
   * Deterministic chatId for two users
   */
  export const makeChatId = (uidA, uidB) => {
    if (!uidA || !uidB) throw new Error("makeChatId: both uids required");
    return [uidA, uidB].sort().join("_");
  };
  
  /**
   * Ensure chat doc exists (creates if missing).
   * Returns { chatId, chatRef, created }
   */
  export const ensureChatDoc = async (uidA, uidB) => {
    const chatId = makeChatId(uidA, uidB);
    const chatRef = doc(db, "chats", chatId);
    const snap = await getDoc(chatRef);
    if (!snap.exists()) {
      await setDoc(chatRef, {
        participants: [uidA, uidB],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null
      });
      return { chatId, chatRef, created: true };
    }
    return { chatId, chatRef, created: false };
  };
  
  /**
   * Send message to a chat:
   * - Adds doc in chats/{chatId}/messages
   * - Updates chats/{chatId}.lastMessage and updatedAt (merge)
   *
   * message: { text?, senderId, senderName?, imageUrl?, fileUrl?, ... }
   * Returns id of created message.
   */
  export const sendMessageToChat = async (chatId, message) => {
    if (!chatId || !message || !message.senderId) throw new Error("sendMessageToChat: chatId and message.senderId required");
  
    const messagesCol = collection(db, "chats", chatId, "messages");
  
    const messageDoc = {
      ...message,
      timestamp: serverTimestamp(),
      isRead: false
    };
  
    // add message doc
    const added = await addDoc(messagesCol, messageDoc);
  
    // update chat doc's lastMessage and updatedAt (merge)
    const chatRef = doc(db, "chats", chatId);
    try {
      await updateDoc(chatRef, {
        lastMessage: {
          text: message.text || "",
          senderId: message.senderId,
          senderName: message.senderName || "",
          timestamp: serverTimestamp(),
          isRead: false
        },
        updatedAt: serverTimestamp(),
        participants: message.participants || undefined // optional
      });
    } catch (err) {
      // if chat doc didn't exist, set it
      await setDoc(chatRef, {
        participants: message.participants || [],
        lastMessage: {
          text: message.text || "",
          senderId: message.senderId,
          senderName: message.senderName || "",
          timestamp: serverTimestamp(),
          isRead: false
        },
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, { merge: true });
    }
  
    return added.id;
  };
  
  /**
   * Listen to messages in a chat (ordered asc by timestamp).
   * onUpdate(messagesArray)
   * Returns unsubscribe function.
   */
  export const listenToMessages = (chatId, onUpdate, onError) => {
    if (!chatId) throw new Error("listenToMessages: chatId required");
    const q = query(collection(db, "chats", chatId, "messages"), orderBy("timestamp", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      const messages = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onUpdate(messages);
    }, (err) => {
      if (onError) onError(err);
      else console.error("listenToMessages error:", err);
    });
    return unsub;
  };
  
  /**
   * Mark unread messages as read for currentUserId in chatId.
   * Batch updates message.isRead and chat.lastMessage.isRead.
   */
  export const markChatMessagesRead = async (chatId, currentUserId) => {
    if (!chatId || !currentUserId) return;
  
    const messagesCol = collection(db, "chats", chatId, "messages");
    const q = query(messagesCol, where("isRead", "==", false), where("senderId", "!=", currentUserId));
    const snap = await getDocs(q);
  
    if (snap.empty) {
      // still possibly update chat.lastMessage if needed
      const chatRef = doc(db, "chats", chatId);
      const chatSnap = await getDoc(chatRef);
      if (chatSnap.exists()) {
        const data = chatSnap.data();
        if (data?.lastMessage && data.lastMessage.senderId !== currentUserId && data.lastMessage.isRead === false) {
          await updateDoc(chatRef, { "lastMessage.isRead": true });
        }
      }
      return;
    }
  
    const batch = writeBatch(db);
    snap.docs.forEach(d => {
      const msgRef = doc(db, "chats", chatId, "messages", d.id);
      batch.update(msgRef, { isRead: true });
    });
  
    const chatRef = doc(db, "chats", chatId);
    batch.update(chatRef, { "lastMessage.isRead": true });
  
    await batch.commit();
  };
  
  /**
   * Listen to chats where user is a participant.
   * onUpdate receives array of chat docs [{ id, ...data }]
   * Useful for computing unread counts via lastMessage or by separate queries.
   */
  export const listenToUserChats = (userId, onUpdate, onError) => {
    if (!userId) throw new Error("listenToUserChats: userId required");
    const q = query(collection(db, "chats"), where("participants", "array-contains", userId));
    const unsub = onSnapshot(q, (snap) => {
      const chats = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onUpdate(chats);
    }, (err) => {
      if (onError) onError(err);
      else console.error("listenToUserChats error:", err);
    });
    return unsub;
  };
  
  /**
   * Typing indicator helpers: documents under chats/{chatId}/typing/{userId}
   */
  export const setTyping = async (chatId, userId) => {
    if (!chatId || !userId) return;
    const typingRef = doc(db, "chats", chatId, "typing", userId);
    await setDoc(typingRef, { uid: userId, timestamp: serverTimestamp() });
  };
  
  export const clearTyping = async (chatId, userId) => {
    if (!chatId || !userId) return;
    try {
      await deleteDoc(doc(db, "chats", chatId, "typing", userId));
    } catch (e) { /* ignore not-found */ }
  };
  
  export const listenTyping = (chatId, onUpdate, onError) => {
    if (!chatId) throw new Error("listenTyping: chatId required");
    const unsub = onSnapshot(collection(db, "chats", chatId, "typing"), (snap) => {
      const list = snap.docs.map(d => d.id); // array of userIds typing
      onUpdate(list);
    }, (err) => {
      if (onError) onError(err);
      else console.error("listenTyping error:", err);
    });
    return unsub;
  };
  
  /**
   * User presence helpers (userStatus collection)
   */
  export const setUserOnline = async (userId, displayName = "") => {
    if (!userId) return;
    const ref = doc(db, "userStatus", userId);
    await setDoc(ref, { uid: userId, status: "online", lastSeen: serverTimestamp(), displayName }, { merge: true });
  };
  
  export const setUserOffline = async (userId, displayName = "") => {
    if (!userId) return;
    const ref = doc(db, "userStatus", userId);
    await setDoc(ref, { uid: userId, status: "offline", lastSeen: serverTimestamp(), displayName }, { merge: true });
  };
  
  export const listenUserStatus = (onUpdate, onError) => {
    const unsub = onSnapshot(collection(db, "userStatus"), (snap) => {
      const statuses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onUpdate(statuses);
    }, (err) => {
      if (onError) onError(err);
      else console.error("listenUserStatus error:", err);
    });
    return unsub;
  };
  
  /**
   * Listen users collection (onSnapshot). Returns unsubscribe.
   */
  export const listenUsers = (onUpdate, onError) => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onUpdate(users);
    }, (err) => {
      if (onError) onError(err);
      else console.error("listenUsers error:", err);
    });
    return unsub;
  };
  
  /**
   * Safe delete doc helper to avoid unhandled errors
   */
  export const safeDeleteDoc = async (refDoc) => {
    try { await deleteDoc(refDoc); } catch (e) { /* ignore */ }
  };
  